import React, { useState, useEffect, useCallback } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from 'recharts';
import * as api from './api';
import './App.css';

const COLOURS = ['#1a365d', '#2563eb', '#3b82f6', '#60a5fa', '#93c5fd', '#bfdbfe', '#cbd5e1', '#94a3b8'];
// Regulators — grouped ones show their tribunal in parentheses
const REGULATORS = [
  { value: 'All', label: 'All Regulators' },
  { value: 'NMC', label: 'NMC' },
  { value: 'GMC', label: 'GMC (incl. MPTS)' },
  { value: 'HCPC', label: 'HCPC (incl. HCPTS)' },
  { value: 'GDC', label: 'GDC (incl. Dental Tribunal)' },
  { value: 'GPhC', label: 'GPhC' },
  { value: 'SRA', label: 'SRA (incl. SDT)' },
  { value: 'BSB', label: 'BSB' },
  { value: 'GOC', label: 'GOC' },
  { value: 'GOsC', label: 'GOsC' },
  { value: 'GCC', label: 'GCC' },
  { value: 'SWE', label: 'SWE' },
  { value: 'BACP', label: 'BACP' },
  { value: 'UKCP', label: 'UKCP' },
];

type Mode = 'intelligence' | 'studio';
type IntelTab = 'feed' | 'overview' | 'benchmarking' | 'recent' | 'insights' | 'representation';
type StudioTab = 'si-queue' | 'sls-queue' | 'knowledge-queue' | 'sources' | 'prompts' | 'costs' | 'linkedin' | 'scenarios' | 'analytics';

const ACCESS_CODE = 'sequentus2026';

function App() {
  const [authenticated, setAuthenticated] = useState(() => sessionStorage.getItem('si_auth') === 'true');
  const [codeInput, setCodeInput] = useState('');
  const [mode, setMode] = useState<Mode>('intelligence');
  const [intelTab, setIntelTab] = useState<IntelTab>('feed');
  const [studioTab, setStudioTab] = useState<StudioTab>('si-queue');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<any>(null);

  // Only load data for feed and studio tabs (other intel tabs manage their own)
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      if (mode === 'intelligence' && intelTab === 'feed') {
        setData(await api.getFeed(30));
      } else if (mode === 'studio') {
        const channelMap: Record<string, string> = { 'si-queue': 'si', 'sls-queue': 'sls', 'knowledge-queue': 'knowledge' };
        const channel = channelMap[studioTab];
        if (channel) setData(await api.getEditorialQueue(channel));
      }
    } catch (e) { console.error('API error:', e); }
    setLoading(false);
  }, [mode, intelTab, studioTab]);

  useEffect(() => { loadData(); }, [loadData]);

  if (!authenticated) {
    return (
      <div className="app">
        <div className="auth-gate">
          <div className="auth-brand">
            <img src="/logo.png" alt="Sequentus" className="auth-logo" />
            <span className="auth-sub">Intelligence</span>
          </div>
          <p>Enter access code to continue</p>
          <form onSubmit={e => { e.preventDefault(); if (codeInput === ACCESS_CODE) { sessionStorage.setItem('si_auth', 'true'); setAuthenticated(true); } else { alert('Invalid code'); }}}>
            <input type="password" value={codeInput} onChange={e => setCodeInput(e.target.value)} placeholder="Access code" autoFocus />
            <button type="submit" className="btn btn-primary">Enter</button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="app">
      <Header mode={mode} setMode={setMode} />

      {mode === 'intelligence' ? (
        <>
          <Nav tabs={['feed', 'overview', 'benchmarking', 'recent', 'insights', 'representation']}
               labels={{ representation: 'Represented vs Not' }}
               active={intelTab} onSelect={(t: string) => setIntelTab(t as IntelTab)} />
          <main className="content">
            {intelTab === 'feed' ? (loading ? <Loading /> : <FeedView data={data} />) :
              intelTab === 'overview' ? <OverviewView /> :
              intelTab === 'benchmarking' ? <BenchmarkingView /> :
              intelTab === 'recent' ? <RecentView /> :
              intelTab === 'insights' ? <InsightsView /> :
              intelTab === 'representation' ? <RepresentationView /> : null}
          </main>
        </>
      ) : (
        <>
          <Nav tabs={['si-queue', 'sls-queue', 'knowledge-queue', 'sources', 'scenarios', 'prompts', 'costs', 'linkedin', 'analytics']}
               labels={{ 'si-queue': 'SI Editorial', 'sls-queue': 'SLS Editorial', 'knowledge-queue': 'Knowledge', 'sources': 'Sources', 'scenarios': 'Scenarios', 'prompts': 'Prompts', 'costs': 'Costs', 'linkedin': 'LinkedIn', 'analytics': 'Analytics' }}
               active={studioTab} onSelect={(t: string) => setStudioTab(t as StudioTab)} />
          <main className="content">
            {loading ? <Loading /> :
              studioTab === 'sources' ? <SourcesView /> :
              studioTab === 'prompts' ? <PromptsView /> :
              studioTab === 'costs' ? <CostsView /> :
              studioTab === 'scenarios' ? <ScenariosView /> :
              studioTab === 'linkedin' ? <LinkedInView /> :
              studioTab === 'analytics' ? <AnalyticsView /> :
              <QueueView data={data} channel={studioTab.replace('-queue', '')} onRefresh={loadData} />}
          </main>
        </>
      )}

      <footer className="footer">Sequentus Intelligence &copy; {new Date().getFullYear()}</footer>
    </div>
  );
}

// ── Shared components ────────────────────────────────────────────────────────

function Header({ mode, setMode }: any) {
  return (
    <header className="header">
      <div className="header-left">
        <div className="header-brand">
          <img src="/logo.png" alt="Sequentus" className="header-logo" />
          <span className="header-sub">Intelligence</span>
        </div>
        <span className="badge">Beta</span>
        <div className="mode-toggle">
          <button className={mode === 'intelligence' ? 'active' : ''} onClick={() => setMode('intelligence')}>Intelligence</button>
          <button className={mode === 'studio' ? 'active' : ''} onClick={() => setMode('studio')}>Studio</button>
        </div>
      </div>
      {/* Global filter removed — each section has its own filters */}
    </header>
  );
}

function Nav({ tabs, labels, active, onSelect }: { tabs: string[]; labels?: Record<string, string>; active: string; onSelect: (t: string) => void }) {
  return (
    <nav className="tabs">
      {tabs.map(t => (
        <button key={t} className={active === t ? 'active' : ''} onClick={() => onSelect(t)}>
          {labels?.[t] || t.charAt(0).toUpperCase() + t.slice(1)}
        </button>
      ))}
    </nav>
  );
}

function Loading() { return <div className="loading">Loading...</div>; }

const TIME_PERIODS = [
  { value: '', label: 'All time' },
  { value: '30', label: 'Last 30 days' },
  { value: '90', label: 'Last 3 months' },
  { value: '180', label: 'Last 6 months' },
  { value: '365', label: 'Last 12 months' },
  { value: 'ytd', label: 'Year to date' },
];

function computeDateRange(period: string): { from?: string; to?: string } {
  if (!period) return {};
  const now = new Date();
  if (period === 'ytd') {
    return { from: `${now.getFullYear()}-01-01` };
  }
  const days = parseInt(period);
  const from = new Date(now.getTime() - days * 86400000);
  return { from: from.toISOString().split('T')[0] };
}

function SectionFilters({ regulator, setRegulator, period, setPeriod }: {
  regulator: string; setRegulator: (v: string) => void;
  period: string; setPeriod: (v: string) => void;
}) {
  return (
    <div className="section-filters">
      <select value={regulator} onChange={e => setRegulator(e.target.value)} className="filter-select">
        {REGULATORS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
      </select>
      <select value={period} onChange={e => setPeriod(e.target.value)} className="filter-select">
        {TIME_PERIODS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
      </select>
    </div>
  );
}

// ── Intelligence views ───────────────────────────────────────────────────────

function FeedView({ data }: any) {
  const items = data?.items || [];
  if (!items.length) return <div className="empty-state">No feed items yet. The intelligence feed will populate once processing is enabled.</div>;

  return (
    <div className="feed">
      {items.map((item: any) => (
        <div key={item.id} className={`feed-item feed-${item.feed_type}`}>
          <div className="feed-meta">
            <span className={`feed-type-badge ${item.feed_type}`}>{item.feed_type?.replace('_', ' ')}</span>
            <span className="feed-date">{item.created_at?.split('T')[0]}</span>
          </div>
          <h3>{item.title}</h3>
          <div className="feed-body">{item.body?.split('\n').slice(0, 4).map((p: string, i: number) => <p key={i}>{p}</p>)}</div>
        </div>
      ))}
    </div>
  );
}

const SANCTION_LABELS: Record<string, string> = {
  struck_off: 'Struck off', suspended: 'Suspended', conditions: 'Conditions',
  caution: 'Caution', no_case: 'No case to answer', no_order: 'No order made',
  fine: 'Fine', voluntary_removal: 'Voluntary removal',
  reprimand: 'Reprimand', restored: 'Restored', other: 'Other',
};

function OverviewView() {
  const [pieReg, setPieReg] = useState('All');
  const [piePeriod, setPiePeriod] = useState('');
  const [allRows, setAllRows] = useState<any[]>([]); // always all regulators
  const [pieRows, setPieRows] = useState<any[]>([]); // filtered for pie charts
  const [loading, setLoading] = useState(true);

  // Bar chart: always all regulators, all time
  useEffect(() => {
    api.getSanctionStats().then(d => { setAllRows(d.data || []); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  // Pie charts: filtered
  useEffect(() => {
    const r = pieReg === 'All' ? undefined : pieReg;
    const { from, to } = computeDateRange(piePeriod);
    api.getSanctionStats(r, from, to).then(d => setPieRows(d.data || [])).catch(() => {});
  }, [pieReg, piePeriod]);

  const rows = allRows; // bar chart uses all data

  // Filter by stage
  // Note: the API returns aggregated data, so stage filtering needs to happen server-side
  // For now, filter the sanction breakdown to exclude nulls
  const sanctionPie = rows
    .filter((row: any) => row.sanction) // exclude null sanctions
    .reduce((acc: any[], row: any) => {
      const label = SANCTION_LABELS[row.sanction] || row.sanction;
      const ex = acc.find((a: any) => a.name === label);
      if (ex) ex.value += row.count; else acc.push({ name: label, value: row.count });
      return acc;
    }, []).sort((a: any, b: any) => b.value - a.value);

  const regulatorBar = rows.reduce((acc: any[], row: any) => {
    const ex = acc.find((a: any) => a.regulator === row.regulator);
    if (ex) ex.count += row.count; else acc.push({ regulator: row.regulator, count: row.count });
    return acc;
  }, []).sort((a: any, b: any) => b.count - a.count);

  // Stacked bar — regulator x stage
  const stackedBar = rows.reduce((acc: any[], row: any) => {
    let ex = acc.find((a: any) => a.regulator === row.regulator);
    if (!ex) { ex = { regulator: row.regulator, substantive: 0, interim: 0, other: 0 }; acc.push(ex); }
    if (row.stage_reached === 'interim_order') ex.interim += row.count;
    else if (row.stage_reached === 'substantive' || !row.stage_reached) ex.substantive += row.count;
    else ex.other += row.count;
    return acc;
  }, []).sort((a: any, b: any) => (b.substantive + b.interim + b.other) - (a.substantive + a.interim + a.other));

  // Summary stats
  const totalOutcomes = regulatorBar.reduce((s: number, r: any) => s + r.count, 0);
  const totalWithSanction = sanctionPie.reduce((s: number, r: any) => s + r.value, 0);
  const totalPending = totalOutcomes - totalWithSanction;

  if (loading) return <Loading />;
  if (!rows.length) return <div className="empty-state">No outcome data yet.</div>;

  return (
    <div>
      <div className="overview-stats">
        <div className="stat-mini"><span className="stat-num">{totalOutcomes}</span><span className="stat-label">Total outcomes</span></div>
        <div className="stat-mini"><span className="stat-num">{totalWithSanction}</span><span className="stat-label">With sanction</span></div>
        <div className="stat-mini"><span className="stat-num">{totalPending}</span><span className="stat-label">Pending / referred</span></div>
        <div className="stat-mini"><span className="stat-num">{regulatorBar.length}</span><span className="stat-label">Regulators</span></div>
      </div>
      <div className="card wide" style={{ marginBottom: 16 }}>
        <h2>Outcomes by Regulator</h2>
        <ResponsiveContainer width="100%" height={Math.max(200, stackedBar.length * 45)}>
          <BarChart data={stackedBar} layout="vertical" margin={{ left: 60 }}>
            <XAxis type="number" /><YAxis type="category" dataKey="regulator" width={50} />
            <Tooltip />
            <Legend />
            <Bar dataKey="substantive" stackId="a" fill="#1a365d" name="Substantive" radius={[0, 0, 0, 0]} />
            <Bar dataKey="interim" stackId="a" fill="#3b82f6" name="Interim" />
            <Bar dataKey="other" stackId="a" fill="#93c5fd" name="Other" radius={[0, 4, 4, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
      <SectionFilters regulator={pieReg} setRegulator={setPieReg} period={piePeriod} setPeriod={setPiePeriod} />
      <SanctionPieCharts rows={pieRows} />
    </div>
  );
}

function SanctionPieCharts({ rows, regulator }: { rows: any[]; regulator?: string }) {
  function buildPie(filterFn: (r: any) => boolean, label: string) {
    const filtered = rows.filter(filterFn).filter((r: any) => r.sanction);
    const pie = filtered.reduce((acc: any[], row: any) => {
      const name = SANCTION_LABELS[row.sanction] || row.sanction;
      const ex = acc.find((a: any) => a.name === name);
      if (ex) ex.value += row.count; else acc.push({ name, value: row.count });
      return acc;
    }, []).sort((a: any, b: any) => b.value - a.value);
    const total = pie.reduce((s: number, r: any) => s + r.value, 0);
    return { pie, total, label };
  }

  const substantive = buildPie(
    (r: any) => r.stage_reached === 'substantive' || (!r.stage_reached && r.sanction && !['interim_order', 'review', 'case_examiner', 'appeal'].includes(r.stage_reached)),
    'Substantive Hearings'
  );
  const interim = buildPie(
    (r: any) => r.stage_reached === 'interim_order',
    'Interim Orders'
  );
  const other = buildPie(
    (r: any) => r.stage_reached && !['substantive', 'interim_order'].includes(r.stage_reached),
    'Other (Reviews, Appeals, Case Examiners)'
  );

  const charts = [substantive, interim, other].filter(c => c.total > 0);
  if (!charts.length) return <p className="muted">No sanction data available yet</p>;

  return (
    <div className="grid-3">
      {charts.map((chart, ci) => (
        <div key={ci} className="card">
          <h2>{chart.label} <span className="chart-total">({chart.total})</span></h2>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie data={chart.pie} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90}
                   label={(p: any) => `${p.name} (${p.value})`} labelLine={false}>
                {chart.pie.map((_: any, i: number) => <Cell key={i} fill={COLOURS[i % COLOURS.length]} />)}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
      ))}
    </div>
  );
}

function BenchmarkingView() {
  const [reg, setReg] = useState('All');
  const [period, setPeriod] = useState('');
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const { from, to } = computeDateRange(period);
    api.getBenchmarking(from, to).then(d => { setRows(d.data || []); setLoading(false); }).catch(() => setLoading(false));
  }, [period]);

  if (loading) return <Loading />;
  if (!rows.length) return (
    <div><SectionFilters regulator={reg} setRegulator={setReg} period={period} setPeriod={setPeriod} /><div className="empty-state">No benchmarking data yet.</div></div>
  );

  // Filter by regulator locally
  const filtered = reg === 'All' ? rows : rows.filter((r: any) => r.regulator === reg);

  // Group by stage
  const substantive = filtered.filter((r: any) => r.stage_group === 'substantive');
  const interim = filtered.filter((r: any) => r.stage_group === 'interim');
  const other = filtered.filter((r: any) => r.stage_group === 'other');

  const renderRows = (items: any[], showNoOrder: boolean) => items.map((r: any) => (
    <tr key={`${r.regulator}-${r.stage_group}`}>
      <td className="bold">{r.regulator}</td><td>{r.total_cases}</td>
      <td>{r.struck_off || ''}</td><td>{r.suspended || ''}</td><td>{r.conditions || ''}</td>
      <td>{r.caution || ''}</td>
      {showNoOrder ? <td>{r.no_order || ''}</td> : <td>{r.no_case || ''}</td>}
      <td>{r.fine || ''}</td>
      <td>{r.total_cases ? ((r.impairment_found / r.total_cases) * 100).toFixed(0) + '%' : '-'}</td>
      <td>{r.total_cases ? ((r.legally_represented / r.total_cases) * 100).toFixed(0) + '%' : '-'}</td>
      <td>{r.avg_significance?.toFixed(1) || '-'}</td>
    </tr>
  ));

  return (
    <div>
    <SectionFilters regulator={reg} setRegulator={setReg} period={period} setPeriod={setPeriod} />
    <div className="card wide">
      <h2>Cross-Regulator Benchmarking</h2>
      <div className="table-wrap">
        <table>
          {substantive.length > 0 && <>
            <thead><tr><th colSpan={11} className="group-heading">Substantive Hearings</th></tr>
            <tr>
              <th>Regulator</th><th>Total</th><th>Struck Off</th><th>Suspended</th><th>Conditions</th>
              <th>Caution</th><th>No Case</th><th>Fine</th><th>Impairment %</th><th>Represented %</th><th>Avg Sig</th>
            </tr></thead>
            <tbody>{renderRows(substantive, false)}</tbody>
          </>}

          {interim.length > 0 && <>
            <thead><tr><th colSpan={11} className="group-heading">Interim Orders</th></tr>
            <tr>
              <th>Regulator</th><th>Total</th><th>Struck Off</th><th>Suspended</th><th>Conditions</th>
              <th>Caution</th><th>No Order</th><th>Fine</th><th>Impairment %</th><th>Represented %</th><th>Avg Sig</th>
            </tr></thead>
            <tbody>{renderRows(interim, true)}</tbody>
          </>}

          {other.length > 0 && <>
            <thead><tr><th colSpan={11} className="group-heading">Other (Reviews, Appeals, Case Examiners)</th></tr>
            <tr>
              <th>Regulator</th><th>Total</th><th>Struck Off</th><th>Suspended</th><th>Conditions</th>
              <th>Caution</th><th>No Case</th><th>Fine</th><th>Impairment %</th><th>Represented %</th><th>Avg Sig</th>
            </tr></thead>
            <tbody>{renderRows(other, false)}</tbody>
          </>}
        </table>
      </div>
    </div>
    </div>
  );
}

function RecentView() {
  const [reg, setReg] = useState('All');
  const [period, setPeriod] = useState('');
  const [outcomes, setOutcomes] = useState<any[]>([]);
  const [judgments, setJudgments] = useState<any[]>([]);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const r = reg === 'All' ? undefined : reg;
    Promise.all([
      api.getRecent(30, r),
      api.getRecentJudgments(20, r),
    ]).then(([ho, cj]) => {
      setOutcomes(ho.data || []);
      setJudgments(cj.data || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [reg, period]);

  if (loading) return <Loading />;

  return (
    <div>
    <SectionFilters regulator={reg} setRegulator={setReg} period={period} setPeriod={setPeriod} />
    <div className="recent-columns">
      <div className="recent-col">
        <h2>Hearing Outcomes</h2>
        {!outcomes.length ? <p className="muted">No hearing outcomes yet.</p> :
          outcomes.map((r: any) => (
            <div key={r.id} className="case-card" onClick={() => setExpanded(expanded === r.id ? null : r.id)}>
              <div className="case-header">
                <div className="case-title">
                  <strong>{r.registrant_name || 'Unknown'}</strong>
                  <span className="case-meta">{r.regulator} &middot; {r.profession || ''} &middot; {r.decision_date?.split('T')[0] || ''}</span>
                </div>
                <div className="case-badges">
                  {r.sanction && <span className={`badge-sanction ${r.sanction}`}>{r.sanction?.replace('_', ' ')}</span>}
                  {r.charge_category_primary && <span className="badge-charge">{r.charge_category_primary}</span>}
                  <span className="case-sig">{'★'.repeat(r.significance_score || 0)}{'☆'.repeat(5 - (r.significance_score || 0))}</span>
                </div>
              </div>
              {r.haiku_summary && <p className="case-summary">{r.haiku_summary}</p>}
              {expanded === r.id && (
                <div className="case-detail">
                  {r.significance_reason && <p className="case-reason"><strong>Why notable:</strong> {r.significance_reason}</p>}
                  {r.mitigating_factors && <p><strong>Mitigating:</strong> {r.mitigating_factors}</p>}
                  {r.aggravating_factors && <p><strong>Aggravating:</strong> {r.aggravating_factors}</p>}
                  {r.impairment_ground && <p><strong>Impairment ground:</strong> {r.impairment_ground?.replace('_', ' ')}</p>}
                  {r.insight_finding && <p><strong>Insight:</strong> {r.insight_finding}</p>}
                </div>
              )}
            </div>
          ))}
      </div>

      <div className="recent-col">
        <h2>Court Judgments</h2>
        {!judgments.length ? <p className="muted">No court judgments yet.</p> :
          judgments.map((r: any) => (
            <div key={r.id} className="case-card judgment-card" onClick={() => setExpanded(expanded === r.id ? null : r.id)}>
              <div className="case-header">
                <div className="case-title">
                  <strong>{r.citation || r.title?.slice(0, 40) || 'Unknown'}</strong>
                  <span className="case-meta">{r.court || ''} &middot; {r.judgment_date?.split('T')[0] || ''} &middot; {r.regulators || ''}</span>
                </div>
                <span className="case-sig">{'★'.repeat(r.significance_score || 0)}{'☆'.repeat(5 - (r.significance_score || 0))}</span>
              </div>
              {r.haiku_summary && <p className="case-summary">{r.haiku_summary}</p>}
              {expanded === r.id && (
                <div className="case-detail">
                  {r.key_legal_principles && <p><strong>Legal principles:</strong> {r.key_legal_principles}</p>}
                  {r.judge_name && <p><strong>Judge:</strong> {r.judge_name}</p>}
                  {r.costs_order && <p><strong>Costs:</strong> {r.costs_order}</p>}
                </div>
              )}
            </div>
          ))}
      </div>
    </div>
    </div>
  );
}

function InsightsView() {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.getInsights().then(d => { setData(d); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  if (loading) return <Loading />;
  if (!data) return <div className="empty-state">No insights yet.</div>;
  return (
    <div className="grid-2">
      <div className="card">
        <h2>Overall</h2>
        <div className="stat-grid">
          <div className="stat"><span className="stat-num">{data.totals?.total?.toLocaleString() || 0}</span><span className="stat-label">Total Outcomes</span></div>
          <div className="stat"><span className="stat-num">{data.totals?.regulators || 0}</span><span className="stat-label">Regulators</span></div>
          <div className="stat"><span className="stat-num">{data.totals?.earliest?.split('T')[0] || '-'}</span><span className="stat-label">Earliest</span></div>
          <div className="stat"><span className="stat-num">{data.totals?.latest?.split('T')[0] || '-'}</span><span className="stat-label">Latest</span></div>
        </div>
      </div>
      <div className="card">
        <h2>Charge Categories</h2>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={data.charges || []} layout="vertical" margin={{ left: 80 }}>
            <XAxis type="number" /><YAxis type="category" dataKey="charge_category_primary" width={70} />
            <Tooltip /><Bar dataKey="count" fill="#2563eb" radius={[0, 4, 4, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="card wide">
        <h2>Landmark Cases (Significance 4-5)</h2>
        <div className="table-wrap">
          <table>
            <thead><tr><th>Date</th><th>Regulator</th><th>Profession</th><th>Charge</th><th>Sanction</th><th>Score</th></tr></thead>
            <tbody>
              {(data.landmark || []).map((r: any) => (
                <tr key={r.id}>
                  <td>{r.decision_date?.split('T')[0]}</td><td>{r.regulator}</td><td>{r.profession}</td>
                  <td>{r.charge_category_primary}</td><td>{r.sanction}</td><td>{'★'.repeat(r.significance_score)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ── Studio views ─────────────────────────────────────────────────────────────

function QueueView({ data, channel, onRefresh }: { data: any; channel: string; onRefresh: () => void }) {
  const items = data?.items || [];
  const [generating, setGenerating] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);

  const channelLabels: Record<string, string> = { si: 'SI Editorial', sls: 'SLS Editorial', knowledge: 'Knowledge' };

  async function handleGenerate(id: string) {
    setGenerating(id);
    try {
      await api.generateContent(id);
      onRefresh();
    } catch (e) { console.error('Generate error:', e); }
    setGenerating(null);
  }

  async function handleApprove(id: string) {
    const user = prompt('Your name for approval:');
    if (!user) return;
    try {
      await api.approveItem(id, user);
      onRefresh();
    } catch (e) { console.error('Approve error:', e); }
  }

  async function handleReject(id: string) {
    try {
      await api.rejectItem(id);
      onRefresh();
    } catch (e) { console.error('Reject error:', e); }
  }

  if (!items.length) return (
    <div className="empty-state">
      No items in the {channelLabels[channel] || channel} queue. Items will appear here when the Sonnet daily pass identifies content worth reviewing.
    </div>
  );

  return (
    <div className="queue">
      <h2>{channelLabels[channel] || channel} Queue</h2>
      {items.map((item: any) => (
        <div key={item.id} className={`queue-item status-${item.status}`}>
          <div className="queue-header" onClick={() => setExpanded(expanded === item.id ? null : item.id)}>
            <div className="queue-title">
              <span className={`status-badge ${item.status}`}>{item.status}</span>
              <strong>{item.sonnet_reason}</strong>
            </div>
            <span className="queue-date">{item.created_at?.split('T')[0]}</span>
          </div>

          {expanded === item.id && (
            <div className="queue-detail">
              <p className="queue-summary">{item.sonnet_summary}</p>
              <div className="queue-meta">
                <span>Source: {item.source_type}</span>
                <span>Content types: {item.content_type}</span>
              </div>

              {item.generated_content && (
                <div className="queue-generated">
                  <h4>Generated Content</h4>
                  <pre>{typeof item.generated_content === 'string' ? item.generated_content : JSON.stringify(JSON.parse(item.generated_content), null, 2)}</pre>
                </div>
              )}

              <div className="queue-actions">
                {item.status === 'pending' && (
                  <button className="btn btn-primary" onClick={() => handleGenerate(item.id)} disabled={generating === item.id}>
                    {generating === item.id ? 'Generating...' : 'Generate Content'}
                  </button>
                )}
                {item.status === 'generated' && (
                  <>
                    <button className="btn btn-success" onClick={() => handleApprove(item.id)}>Approve &amp; Publish</button>
                    <button className="btn btn-danger" onClick={() => handleReject(item.id)}>Reject</button>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function ManualUpload() {
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const API_BASE = process.env.REACT_APP_API_BASE || 'https://sequentus-content.azurewebsites.net/api';

  async function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files[0];
    if (!file) return;
    await uploadFile(file);
  }

  async function handleFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    await uploadFile(file);
  }

  async function uploadFile(file: File) {
    setUploading(true);
    setResult(null);
    try {
      const res = await fetch(`${API_BASE}/content/upload`, {
        method: 'POST',
        headers: { 'x-filename': file.name, 'Content-Type': file.type || 'application/octet-stream' },
        body: file,
      });
      const data = await res.json();
      if (data.ok) setResult(`Uploaded: ${file.name} (${(file.size / 1024).toFixed(0)} KB)`);
      else setResult(`Error: ${data.error}`);
    } catch (e: any) { setResult(`Upload failed: ${e.message}`); }
    setUploading(false);
  }

  return (
    <div className={`upload-zone ${dragging ? 'dragging' : ''}`}
         onDragOver={e => { e.preventDefault(); setDragging(true); }}
         onDragLeave={() => setDragging(false)}
         onDrop={handleDrop}>
      <input type="file" id="file-upload" style={{ display: 'none' }} onChange={handleFileSelect} accept=".pdf,.doc,.docx,.xml,.html,.txt" />
      <label htmlFor="file-upload" className="upload-label">
        {uploading ? 'Uploading...' : 'Drop a file here or click to upload'}
      </label>
      {result && <p className="upload-result">{result}</p>}
    </div>
  );
}

function SourcesView() {
  const [sources, setSources] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [checking, setChecking] = useState<string | null>(null);

  const healthLabels: Record<number, string> = { 100000000: 'green', 100000001: 'amber', 100000002: 'red' };
  const freqLabels: Record<number, string> = { 100000000: 'Twice daily', 100000001: 'Daily', 100000002: 'Weekly', 100000003: 'Monthly' };
  const formatLabels: Record<number, string> = { 100000000: 'HTML', 100000001: 'PDF', 100000002: 'RSS', 100000003: 'API', 100000004: 'Google Search' };
  const categoryLabels: Record<number, string> = { 100000000: 'Statutory regulator', 100000001: 'Other regulator', 100000002: 'Oversight', 100000003: 'Courts', 100000004: 'Government', 100000005: 'Parliament', 100000006: 'Press', 100000007: 'Research' };

  useEffect(() => {
    api.getSources().then(d => { setSources(d.sources || []); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  async function handleCheck(id: string) {
    setChecking(id);
    try {
      const result = await api.checkSource(id);
      alert(`Check complete: ${result.newItems || 0} new items found`);
      const updated = await api.getSources();
      setSources(updated.sources || []);
    } catch (e: any) { alert(`Check failed: ${e.message}`); }
    setChecking(null);
  }

  async function handleToggle(id: string, currentActive: boolean) {
    await api.updateSource(id, { active: !currentActive });
    const updated = await api.getSources();
    setSources(updated.sources || []);
  }

  async function handleAdd(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const data = Object.fromEntries(new FormData(form));
    try {
      await api.createSource({
        name: data.name, url: data.url, organisation: data.organisation,
        category: parseInt(data.category as string), contenttype: parseInt(data.contenttype as string),
        priority: parseInt(data.priority as string), frequency: parseInt(data.frequency as string),
        format: parseInt(data.format as string), notes: data.notes,
      });
      setShowAdd(false);
      const updated = await api.getSources();
      setSources(updated.sources || []);
    } catch (e: any) { alert(`Create failed: ${e.message}`); }
  }

  if (loading) return <Loading />;

  const green = sources.filter(s => s.crb98_healthstatus === 100000000 || !s.crb98_healthstatus).length;
  const amber = sources.filter(s => s.crb98_healthstatus === 100000001).length;
  const red = sources.filter(s => s.crb98_healthstatus === 100000002).length;

  return (
    <div>
      <div className="sources-header">
        <h2>Monitored Sources</h2>
        <div className="sources-stats">
          <span className="health-dot green" /> {green} green
          <span className="health-dot amber" /> {amber} amber
          <span className="health-dot red" /> {red} red
          <button className="btn btn-primary" onClick={() => setShowAdd(!showAdd)}>+ Add Source</button>
        </div>
      </div>

      <ManualUpload />

      {showAdd && (
        <div className="card" style={{ marginBottom: 16 }}>
          <h3>Add New Source</h3>
          <form onSubmit={handleAdd} className="source-form">
            <input name="name" placeholder="Name" required />
            <input name="url" placeholder="URL or search query" required />
            <input name="organisation" placeholder="Organisation" />
            <select name="category"><option value="100000000">Statutory regulator</option><option value="100000001">Other regulator</option><option value="100000002">Oversight</option><option value="100000003">Courts</option><option value="100000004">Government</option><option value="100000005">Parliament</option><option value="100000006">Press</option><option value="100000007">Research</option></select>
            <select name="contenttype"><option value="100000000">Decisions</option><option value="100000001">News</option><option value="100000002">Consultations</option><option value="100000003">Guidance</option><option value="100000004">Legislation</option><option value="100000005">Stats</option><option value="100000006">Press</option><option value="100000007">Research</option></select>
            <select name="priority"><option value="100000000">High</option><option value="100000001">Medium</option><option value="100000002">Low</option></select>
            <select name="frequency"><option value="100000001">Daily</option><option value="100000000">Twice daily</option><option value="100000002">Weekly</option><option value="100000003">Monthly</option></select>
            <select name="format"><option value="100000000">HTML</option><option value="100000001">PDF</option><option value="100000002">RSS</option><option value="100000003">API</option><option value="100000004">Google Search</option></select>
            <textarea name="notes" placeholder="Notes" rows={2} />
            <div className="source-form-actions">
              <button type="submit" className="btn btn-success">Create</button>
              <button type="button" className="btn btn-danger" onClick={() => setShowAdd(false)}>Cancel</button>
            </div>
          </form>
        </div>
      )}

      <div className="card wide">
        <div className="table-wrap">
          <table>
            <thead><tr>
              <th>Status</th><th>Name</th><th>Organisation</th><th>Category</th>
              <th>Format</th><th>Frequency</th><th>Last Checked</th><th>Last New</th><th>Actions</th>
            </tr></thead>
            <tbody>
              {sources.map((s: any) => {
                const health = healthLabels[s.crb98_healthstatus] || 'green';
                return (
                  <tr key={s.crb98_monitoredsourceid} className={!s.crb98_active ? 'inactive-row' : ''}>
                    <td><span className={`health-dot ${health}`} title={`${health} — ${s.crb98_consecutivefailures || 0} failures`} /></td>
                    <td><strong>{s.crb98_name}</strong><br/><span className="muted" style={{fontSize:11}}>{s.crb98_url?.slice(0,60)}</span></td>
                    <td>{s.crb98_organisation}</td>
                    <td>{categoryLabels[s.crb98_category] || '?'}</td>
                    <td>{formatLabels[s.crb98_format] || '?'}</td>
                    <td>{freqLabels[s.crb98_frequency] || '?'}</td>
                    <td>{s.crb98_lastchecked?.split('T')[0] || 'Never'}</td>
                    <td>{s.crb98_lastnewcontent?.split('T')[0] || 'Never'}</td>
                    <td className="source-actions">
                      <button className="btn-sm" onClick={() => handleCheck(s.crb98_monitoredsourceid)} disabled={checking === s.crb98_monitoredsourceid}>
                        {checking === s.crb98_monitoredsourceid ? '...' : 'Check'}
                      </button>
                      <button className="btn-sm" onClick={() => handleToggle(s.crb98_monitoredsourceid, s.crb98_active)}>
                        {s.crb98_active ? 'Pause' : 'Resume'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ── Prompt Library ───────────────────────────────────────────────────────────

function PromptsView() {
  const [prompts, setPrompts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<any>(null);
  const [showAdd, setShowAdd] = useState(false);

  const toneLabels: Record<number, string> = { 100000000: 'Analytical', 100000001: 'Accessible', 100000002: 'Urgent', 100000003: 'Educational', 100000004: 'Authoritative' };
  const contentLabels: Record<number, string> = { 100000000: 'Decisions', 100000001: 'News', 100000002: 'Consultations', 100000003: 'Guidance', 100000004: 'Legislation', 100000005: 'Press' };
  const outputLabels: Record<number, string> = { 100000000: 'LinkedIn (SI)', 100000001: 'LinkedIn (SLS)', 100000002: 'Substack', 100000003: 'SL Reference', 100000004: 'Snippet', 100000005: 'Insight', 100000006: 'Alert' };

  useEffect(() => {
    api.getPrompts().then(d => { setPrompts(d.prompts || []); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  async function loadPromptDetail(id: string) {
    const full = await api.getPrompt(id);
    setEditing(full);
  }

  async function savePrompt() {
    if (!editing) return;
    await api.updatePrompt(editing.crb98_promptlibraryid, {
      prompttext: editing.crb98_prompttext,
      contexttext: editing.crb98_contexttext,
      tone: editing.crb98_tone,
      maxlength: editing.crb98_maxlength,
      active: editing.crb98_active,
      notes: editing.crb98_notes,
    });
    setEditing(null);
    const updated = await api.getPrompts();
    setPrompts(updated.prompts || []);
  }

  async function handleAdd(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = Object.fromEntries(new FormData(e.currentTarget));
    await api.createPrompt({
      name: form.name, contenttype: parseInt(form.contenttype as string),
      outputtype: parseInt(form.outputtype as string), prompttext: form.prompttext,
      contexttext: form.contexttext, tone: parseInt(form.tone as string),
      maxlength: parseInt(form.maxlength as string) || 500, notes: form.notes,
    });
    setShowAdd(false);
    const updated = await api.getPrompts();
    setPrompts(updated.prompts || []);
  }

  if (loading) return <Loading />;

  if (editing) return (
    <div className="card wide">
      <h2>Edit: {editing.crb98_name}</h2>
      <div className="prompt-editor">
        <label>Prompt Text</label>
        <textarea rows={12} value={editing.crb98_prompttext || ''} onChange={e => setEditing({...editing, crb98_prompttext: e.target.value})} />
        <label>Context (prepended to every call)</label>
        <textarea rows={6} value={editing.crb98_contexttext || ''} onChange={e => setEditing({...editing, crb98_contexttext: e.target.value})} />
        <div className="prompt-meta">
          <label>Max words: <input type="number" value={editing.crb98_maxlength || 500} onChange={e => setEditing({...editing, crb98_maxlength: parseInt(e.target.value)})} /></label>
          <label>Active: <input type="checkbox" checked={editing.crb98_active} onChange={e => setEditing({...editing, crb98_active: e.target.checked})} /></label>
        </div>
        <label>Notes</label>
        <textarea rows={3} value={editing.crb98_notes || ''} onChange={e => setEditing({...editing, crb98_notes: e.target.value})} />
        <div className="prompt-actions">
          <button className="btn btn-success" onClick={savePrompt}>Save (v{(editing.crb98_versionnumber_custom || 0) + 1})</button>
          <button className="btn btn-danger" onClick={() => setEditing(null)}>Cancel</button>
        </div>
      </div>
    </div>
  );

  return (
    <div>
      <div className="sources-header">
        <h2>Prompt Library</h2>
        <button className="btn btn-primary" onClick={() => setShowAdd(!showAdd)}>+ Add Prompt</button>
      </div>

      {showAdd && (
        <div className="card" style={{ marginBottom: 16 }}>
          <form onSubmit={handleAdd} className="source-form">
            <input name="name" placeholder="Prompt name (e.g. 'decisions → linkedin_si')" required />
            <select name="contenttype"><option value="100000000">Decisions</option><option value="100000001">News</option><option value="100000002">Consultations</option><option value="100000003">Guidance</option><option value="100000004">Legislation</option><option value="100000005">Press</option></select>
            <select name="outputtype"><option value="100000000">LinkedIn (SI)</option><option value="100000001">LinkedIn (SLS)</option><option value="100000002">Substack</option><option value="100000003">SL Reference</option><option value="100000004">Snippet</option><option value="100000005">Insight</option><option value="100000006">Alert</option></select>
            <select name="tone"><option value="100000000">Analytical</option><option value="100000001">Accessible</option><option value="100000002">Urgent</option><option value="100000003">Educational</option><option value="100000004">Authoritative</option></select>
            <input name="maxlength" type="number" defaultValue={500} placeholder="Max words" />
            <textarea name="prompttext" placeholder="Prompt text..." rows={4} required style={{ gridColumn: '1 / -1' }} />
            <textarea name="contexttext" placeholder="Standing context (optional)" rows={2} style={{ gridColumn: '1 / -1' }} />
            <textarea name="notes" placeholder="Notes" rows={2} style={{ gridColumn: '1 / -1' }} />
            <div className="source-form-actions"><button type="submit" className="btn btn-success">Create</button><button type="button" className="btn btn-danger" onClick={() => setShowAdd(false)}>Cancel</button></div>
          </form>
        </div>
      )}

      <div className="card wide">
        <div className="table-wrap">
          <table>
            <thead><tr><th>Name</th><th>Content</th><th>Output</th><th>Tone</th><th>Active</th><th>Version</th><th></th></tr></thead>
            <tbody>
              {prompts.map((p: any) => (
                <tr key={p.crb98_promptlibraryid}>
                  <td><strong>{p.crb98_name}</strong></td>
                  <td>{contentLabels[p.crb98_contenttype] || '?'}</td>
                  <td>{outputLabels[p.crb98_outputtype] || '?'}</td>
                  <td>{toneLabels[p.crb98_tone] || '?'}</td>
                  <td>{p.crb98_active ? 'Yes' : 'No'}</td>
                  <td>v{p.crb98_versionnumber_custom || 1}</td>
                  <td><button className="btn-sm" onClick={() => loadPromptDetail(p.crb98_promptlibraryid)}>Edit</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ── Cost Dashboard ───────────────────────────────────────────────────────────

function CostsView() {
  const [data, setData] = useState<any>(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.getCosts(days).then(d => { setData(d); setLoading(false); }).catch(() => setLoading(false));
  }, [days]);

  if (loading) return <Loading />;
  if (!data) return <div className="empty-state">No cost data yet. Costs are logged when SI processing is enabled.</div>;

  return (
    <div>
      <div className="sources-header">
        <h2>API Cost Dashboard</h2>
        <select value={days} onChange={e => setDays(parseInt(e.target.value))} className="reg-filter">
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
        </select>
      </div>

      <div className="grid-2">
        <div className="card">
          <h2>Summary</h2>
          <div className="stat-grid">
            <div className="stat"><span className="stat-num">{data.totalCalls}</span><span className="stat-label">API Calls</span></div>
            <div className="stat"><span className="stat-num">{data.totalCostPounds}</span><span className="stat-label">Total Cost</span></div>
            <div className="stat"><span className="stat-num">{(data.totalInputTokens / 1000).toFixed(0)}K</span><span className="stat-label">Input Tokens</span></div>
            <div className="stat"><span className="stat-num">{data.monthlyProjection}</span><span className="stat-label">Monthly Projection</span></div>
          </div>
        </div>

        <div className="card">
          <h2>Cost by Function</h2>
          <div className="table-wrap">
            <table>
              <thead><tr><th>Function</th><th>Calls</th><th>Cost</th><th>Tokens</th></tr></thead>
              <tbody>
                {(data.byFunction || []).map((f: any) => (
                  <tr key={f.function}>
                    <td><strong>{f.function}</strong></td>
                    <td>{f.calls}</td>
                    <td>{(f.cost / 100).toFixed(4)}</td>
                    <td>{((f.input + f.output) / 1000).toFixed(0)}K</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="card wide">
          <h2>Daily Spend</h2>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={data.byDay || []}>
              <XAxis dataKey="day" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="cost" fill="#2563eb" radius={[4, 4, 0, 0]} name="Cost (pence)" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

// ── Analytics Dashboard ──────────────────────────────────────────────────────

function AnalyticsView() {
  const [live, setLive] = useState<any>(null);
  const [snapshots, setSnapshots] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [kwTab, setKwTab] = useState<'SLS' | 'HD' | 'SL'>('SLS');

  useEffect(() => {
    Promise.all([
      api.getAnalyticsLive().catch(() => null),
      api.getAnalyticsSnapshots(12).catch(() => ({ snapshots: [] }))
    ]).then(([l, s]) => {
      setLive(l);
      setSnapshots(s?.snapshots || []);
      setLoading(false);
    });
  }, []);

  if (loading) return <Loading />;
  if (!live) return <div className="empty-state">Unable to load analytics data. Check API connection.</div>;

  const raw = live.raw || {};

  // Overview stats with week-over-week change
  const sumEvents = (arr: any[], name: string) => {
    const row = (arr || []).find((r: any) => r.dimensions[0] === name);
    return row ? parseInt(row.metrics[0]) : 0;
  };
  const currSessions = sumEvents(raw.currentEvents, 'session_start');
  const priorSessions = sumEvents(raw.priorEvents, 'session_start');
  const currUsers = sumEvents(raw.currentEvents, 'first_visit');
  const priorUsers = sumEvents(raw.priorEvents, 'first_visit');
  const currEvents = (raw.currentEvents || []).reduce((s: number, r: any) => s + parseInt(r.metrics[0]), 0);
  const priorEvents = (raw.priorEvents || []).reduce((s: number, r: any) => s + parseInt(r.metrics[0]), 0);

  const funnelMap: Record<string, number> = {};
  (raw.bookingFunnel || []).forEach((r: any) => { funnelMap[r.dimensions[0]] = parseInt(r.metrics[0]); });
  const currBooked = funnelMap['appointment_booked'] || 0;

  const pctChange = (curr: number, prior: number) => {
    if (prior === 0) return curr > 0 ? '+∞' : '—';
    const pct = Math.round(((curr - prior) / prior) * 100);
    return (pct >= 0 ? '+' : '') + pct + '%';
  };
  const changeClass = (curr: number, prior: number) => {
    if (curr > prior) return 'analytics-up';
    if (curr < prior) return 'analytics-down';
    return 'analytics-flat';
  };

  // Booking funnel data
  const funnelSteps = [
    { label: 'Booking intention', key: 'booking_intention' },
    { label: 'Appointment booked', key: 'appointment_booked' },
    { label: 'Payment modal opened', key: 'payment_modal_open' },
    { label: 'Payment complete', key: 'payment_complete' },
  ];
  const maxFunnel = Math.max(...funnelSteps.map(s => funnelMap[s.key] || 0), 1);

  // Channel chart data
  const channelData = (raw.byChannel || []).map((r: any) => ({
    name: r.dimensions[0], sessions: parseInt(r.metrics[0]), users: parseInt(r.metrics[1])
  }));

  // Trend data from snapshots
  const trendData = [...snapshots].reverse().map((s: any) => {
    const sessRow = (s.raw?.currentEvents || []).find((r: any) => r.dimensions[0] === 'session_start');
    const bookRow = (s.raw?.bookingFunnel || []).find((r: any) => r.dimensions[0] === 'appointment_booked');
    return {
      week: `W${s.week}`,
      sessions: sessRow ? parseInt(sessRow.metrics[0]) : 0,
      bookings: bookRow ? parseInt(bookRow.metrics[0]) : 0
    };
  });

  // Organic keywords
  const organicSites = raw.organicBySite || {};

  return (
    <div>
      <div className="sources-header"><h2>Website Analytics</h2><span style={{ fontSize: 12, opacity: 0.6 }}>Live data from GA4 + Search Console · Updated {live.fetchedAt ? new Date(live.fetchedAt).toLocaleTimeString('en-GB') : '—'}</span></div>

      {/* Overview cards */}
      <div className="stat-grid" style={{ marginBottom: 24 }}>
        <div className="stat"><span className="stat-num">{currSessions}</span><span className={changeClass(currSessions, priorSessions)} style={{ fontSize: 13 }}>{pctChange(currSessions, priorSessions)}</span><span className="stat-label">Sessions (7d)</span></div>
        <div className="stat"><span className="stat-num">{currUsers}</span><span className={changeClass(currUsers, priorUsers)} style={{ fontSize: 13 }}>{pctChange(currUsers, priorUsers)}</span><span className="stat-label">New Users (7d)</span></div>
        <div className="stat"><span className="stat-num">{currEvents}</span><span className={changeClass(currEvents, priorEvents)} style={{ fontSize: 13 }}>{pctChange(currEvents, priorEvents)}</span><span className="stat-label">Events (7d)</span></div>
        <div className="stat"><span className="stat-num">{currBooked}</span><span className="stat-label">Bookings (7d)</span></div>
      </div>

      <div className="grid-2">
        {/* Booking funnel */}
        <div className="card">
          <h2>Booking &amp; Payment Funnel</h2>
          {funnelSteps.map((step, i) => {
            const val = funnelMap[step.key] || 0;
            const prevVal = i > 0 ? (funnelMap[funnelSteps[i - 1].key] || 0) : 0;
            const dropoff = i > 0 && prevVal > 0 ? Math.round(((prevVal - val) / prevVal) * 100) : null;
            return (
              <div key={step.key} className="analytics-funnel-row">
                <span className="analytics-funnel-label">{step.label}</span>
                <div className="analytics-funnel-bar-wrap">
                  <div className="analytics-funnel-bar" style={{ width: `${Math.max((val / maxFunnel) * 100, 2)}%` }} />
                </div>
                <span className="analytics-funnel-count">{val}</span>
                {dropoff !== null && dropoff > 0 && <span className="analytics-down" style={{ fontSize: 11, marginLeft: 4 }}>-{dropoff}%</span>}
              </div>
            );
          })}
        </div>

        {/* Contact clicks */}
        <div className="card">
          <h2>Contact Clicks</h2>
          <div className="table-wrap">
            <table>
              <thead><tr><th>Type</th><th>Detail</th><th>Site</th><th>Clicks</th></tr></thead>
              <tbody>
                {(raw.contactEvents || []).map((r: any, i: number) => (
                  <tr key={i}><td>{r.dimensions[0]?.replace('_click', '')}</td><td style={{ fontSize: 12 }}>{r.dimensions[1] || '—'}</td><td>{r.dimensions[2] || '—'}</td><td><strong>{r.metrics[0]}</strong></td></tr>
                ))}
                {(raw.contactEvents || []).length === 0 && <tr><td colSpan={4} style={{ opacity: 0.5 }}>No contact clicks this week</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Traffic by channel */}
      <div className="card wide" style={{ marginTop: 16 }}>
        <h2>Traffic by Channel</h2>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={channelData} layout="vertical">
            <XAxis type="number" tick={{ fontSize: 11 }} />
            <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={120} />
            <Tooltip />
            <Bar dataKey="sessions" fill="#2563eb" radius={[0, 4, 4, 0]} name="Sessions" />
            <Bar dataKey="users" fill="#60a5fa" radius={[0, 4, 4, 0]} name="Users" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Trend chart (from snapshots) */}
      {trendData.length > 1 && (
        <div className="card wide" style={{ marginTop: 16 }}>
          <h2>Weekly Trends</h2>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={trendData}>
              <XAxis dataKey="week" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="sessions" stroke="#2563eb" strokeWidth={2} name="Sessions" />
              <Line type="monotone" dataKey="bookings" stroke="#16a34a" strokeWidth={2} name="Bookings" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="grid-2" style={{ marginTop: 16 }}>
        {/* Top pages */}
        <div className="card">
          <h2>Top Pages</h2>
          <div className="table-wrap">
            <table>
              <thead><tr><th>Page</th><th>Sessions</th></tr></thead>
              <tbody>
                {(raw.topPages || []).map((r: any, i: number) => (
                  <tr key={i}><td style={{ fontSize: 12 }}>{r.dimensions[0]}</td><td><strong>{r.metrics[0]}</strong></td></tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Country breakdown */}
        <div className="card">
          <h2>Traffic by Country</h2>
          <div className="table-wrap">
            <table>
              <thead><tr><th>Country</th><th>Sessions</th><th>Events</th></tr></thead>
              <tbody>
                {(raw.byCountry || []).map((r: any, i: number) => (
                  <tr key={i}><td>{r.dimensions[0]}</td><td>{r.metrics[0]}</td><td>{r.metrics[1]}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Organic keywords */}
      <div className="card wide" style={{ marginTop: 16 }}>
        <h2>Organic Search Terms</h2>
        <div className="analytics-sub-tabs">
          {(['SLS', 'HD', 'SL'] as const).map(s => (
            <button key={s} className={kwTab === s ? 'active' : ''} onClick={() => setKwTab(s)}>{s}</button>
          ))}
        </div>
        <div className="table-wrap">
          <table>
            <thead><tr><th>Keyword</th><th>Clicks</th><th>Impressions</th><th>CTR</th><th>Position</th></tr></thead>
            <tbody>
              {(organicSites[kwTab] || []).map((k: any, i: number) => (
                <tr key={i}><td style={{ fontSize: 12 }}>{k.keyword}</td><td>{k.clicks}</td><td>{k.impressions}</td><td>{k.ctr}%</td><td>{k.position}</td></tr>
              ))}
              {(organicSites[kwTab] || []).length === 0 && <tr><td colSpan={5} style={{ opacity: 0.5 }}>No keyword data for {kwTab}</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* AI Analysis */}
      {(snapshots.length > 0 && snapshots[0]?.analysis) && (
        <div className="card wide" style={{ marginTop: 16 }}>
          <h2>AI Analysis <span style={{ fontSize: 12, opacity: 0.5, fontWeight: 400 }}>Week {snapshots[0].week}</span></h2>
          <div className="analytics-ai-box">{snapshots[0].analysis}</div>
        </div>
      )}
    </div>
  );
}

// ── LinkedIn Scheduler ───────────────────────────────────────────────────────

function LinkedInView() {
  const [status, setStatus] = useState<any>(null);
  const [channel, setChannel] = useState<'si' | 'sls'>('si');
  const [postText, setPostText] = useState('');
  const [posting, setPosting] = useState(false);
  const [result, setResult] = useState<string | null>(null);

  useEffect(() => {
    api.getLinkedInStatus().then(setStatus).catch(() => {});
  }, []);

  async function handlePost() {
    if (!postText) return;
    setPosting(true);
    setResult(null);
    try {
      await api.postToLinkedIn(channel, postText);
      setResult('Posted successfully');
      setPostText('');
    } catch (e: any) { setResult(`Error: ${e.message}`); }
    setPosting(false);
  }

  const siConnected = status?.si?.connected;
  const slsConnected = status?.sls?.connected;

  return (
    <div>
      <h2 style={{ marginBottom: 16 }}>LinkedIn</h2>

      <div className="grid-2" style={{ marginBottom: 16 }}>
        <div className="card">
          <h3>SI LinkedIn</h3>
          {siConnected
            ? <p style={{ color: '#059669' }}>Connected as {status.si.name}</p>
            : <><p className="muted">Not connected</p>
                <a href="https://sequentus-content.azurewebsites.net/api/si/linkedin/auth/si" className="btn btn-primary" style={{ display: 'inline-block', marginTop: 8, textDecoration: 'none' }}>Connect SI LinkedIn</a></>
          }
        </div>
        <div className="card">
          <h3>SLS LinkedIn</h3>
          {slsConnected
            ? <p style={{ color: '#059669' }}>Connected as {status.sls.name}</p>
            : <><p className="muted">Not connected</p>
                <a href="https://sequentus-content.azurewebsites.net/api/si/linkedin/auth/sls" className="btn btn-primary" style={{ display: 'inline-block', marginTop: 8, textDecoration: 'none' }}>Connect SLS LinkedIn</a></>
          }
        </div>
      </div>

      <div className="card wide">
        <h3>Compose Post</h3>
        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          <button className={`btn-sm ${channel === 'si' ? 'selected' : ''}`} onClick={() => setChannel('si')} style={channel === 'si' ? { background: '#1a365d', color: 'white' } : {}}>SI</button>
          <button className={`btn-sm ${channel === 'sls' ? 'selected' : ''}`} onClick={() => setChannel('sls')} style={channel === 'sls' ? { background: '#1a365d', color: 'white' } : {}}>SLS</button>
        </div>
        <textarea value={postText} onChange={e => setPostText(e.target.value)}
          placeholder={`Write a ${channel.toUpperCase()} LinkedIn post...`} rows={8}
          style={{ width: '100%', padding: 12, borderRadius: 8, border: '1px solid #cbd5e1', fontFamily: '-apple-system, sans-serif', fontSize: 14, marginBottom: 12, resize: 'vertical' }} />
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button className="btn btn-primary" onClick={handlePost} disabled={posting || !postText}>
            {posting ? 'Posting...' : `Post to ${channel.toUpperCase()} LinkedIn`}
          </button>
          <span className="muted" style={{ fontSize: 12 }}>{postText.length} chars</span>
        </div>
        {result && <p style={{ marginTop: 8, color: result.startsWith('Error') ? '#dc2626' : '#059669', fontSize: 13 }}>{result}</p>}
      </div>
    </div>
  );
}

// ── Representation Comparison ────────────────────────────────────────────────

function RepresentationView() {
  const [reg, setReg] = useState('All');
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const r = reg === 'All' ? undefined : reg;
    api.getRepresentationComparison(r).then(d => { setData(d); setLoading(false); }).catch(() => setLoading(false));
  }, [reg]);

  if (loading) return <Loading />;
  if (!data?.summary?.length) return (
    <div><SectionFilters regulator={reg} setRegulator={setReg} period="" setPeriod={() => {}} />
    <div className="empty-state">No representation data yet. This view compares outcomes for represented vs unrepresented registrants.</div></div>
  );

  const represented = data.summary.find((s: any) => s.representation === 'Represented') || {};
  const unrepresented = data.summary.find((s: any) => s.representation === 'Unrepresented') || {};

  return (
    <div>
      <SectionFilters regulator={reg} setRegulator={setReg} period="" setPeriod={() => {}} />

      <div className="grid-2" style={{ marginBottom: 16 }}>
        <div className="card">
          <h2>Represented <span className="chart-total">({represented.total || 0} cases)</span></h2>
          <div className="stat-grid">
            <div className="stat"><span className="stat-num">{represented.struck_off || 0}</span><span className="stat-label">Struck off</span></div>
            <div className="stat"><span className="stat-num">{represented.suspended || 0}</span><span className="stat-label">Suspended</span></div>
            <div className="stat"><span className="stat-num">{represented.conditions || 0}</span><span className="stat-label">Conditions</span></div>
            <div className="stat"><span className="stat-num">{represented.no_case || 0}</span><span className="stat-label">No case</span></div>
          </div>
        </div>
        <div className="card">
          <h2>Unrepresented <span className="chart-total">({unrepresented.total || 0} cases)</span></h2>
          <div className="stat-grid">
            <div className="stat"><span className="stat-num">{unrepresented.struck_off || 0}</span><span className="stat-label">Struck off</span></div>
            <div className="stat"><span className="stat-num">{unrepresented.suspended || 0}</span><span className="stat-label">Suspended</span></div>
            <div className="stat"><span className="stat-num">{unrepresented.conditions || 0}</span><span className="stat-label">Conditions</span></div>
            <div className="stat"><span className="stat-num">{unrepresented.no_case || 0}</span><span className="stat-label">No case</span></div>
          </div>
        </div>
      </div>

      <div className="card wide">
        <h2>Outcome Comparison</h2>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={[
            { sanction: 'Struck off', Represented: represented.total ? Math.round((represented.struck_off / represented.total) * 100) : 0, Unrepresented: unrepresented.total ? Math.round((unrepresented.struck_off / unrepresented.total) * 100) : 0 },
            { sanction: 'Suspended', Represented: represented.total ? Math.round((represented.suspended / represented.total) * 100) : 0, Unrepresented: unrepresented.total ? Math.round((unrepresented.suspended / unrepresented.total) * 100) : 0 },
            { sanction: 'Conditions', Represented: represented.total ? Math.round((represented.conditions / represented.total) * 100) : 0, Unrepresented: unrepresented.total ? Math.round((unrepresented.conditions / unrepresented.total) * 100) : 0 },
            { sanction: 'Caution', Represented: represented.total ? Math.round((represented.caution / represented.total) * 100) : 0, Unrepresented: unrepresented.total ? Math.round((unrepresented.caution / unrepresented.total) * 100) : 0 },
            { sanction: 'No case', Represented: represented.total ? Math.round((represented.no_case / represented.total) * 100) : 0, Unrepresented: unrepresented.total ? Math.round((unrepresented.no_case / unrepresented.total) * 100) : 0 },
          ]}>
            <XAxis dataKey="sanction" /><YAxis unit="%" /><Tooltip />
            <Legend />
            <Bar dataKey="Represented" fill="#1a365d" />
            <Bar dataKey="Unrepresented" fill="#c4a35a" />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

// ── Draft Scenarios (Studio) ─────────────────────────────────────────────────

function ScenariosView() {
  const [scenarios, setScenarios] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('draft');

  useEffect(() => {
    setLoading(true);
    api.getDraftScenarios(filter).then(d => { setScenarios(d.scenarios || []); setLoading(false); }).catch(() => setLoading(false));
  }, [filter]);

  async function handlePromote(id: string) {
    await api.promoteScenario(id);
    setScenarios(s => s.filter(sc => sc.id !== id));
  }

  async function handleReject(id: string) {
    await api.rejectScenario(id);
    setScenarios(s => s.filter(sc => sc.id !== id));
  }

  if (loading) return <Loading />;

  return (
    <div>
      <div className="sources-header">
        <h2>Draft Scenarios</h2>
        <select value={filter} onChange={e => setFilter(e.target.value)} className="filter-select">
          <option value="draft">Drafts</option>
          <option value="promoted">Promoted</option>
          <option value="rejected">Rejected</option>
        </select>
      </div>

      {!scenarios.length ? <div className="empty-state">No {filter} scenarios. Draft scenarios are generated automatically by Haiku when processing substantive hearing outcomes.</div> :
        scenarios.map((s: any) => (
          <div key={s.id} className="card" style={{ marginBottom: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <span className="badge-charge">{s.regulator}</span>
                {s.charge_category && <span className="badge-charge" style={{ marginLeft: 4 }}>{s.charge_category}</span>}
              </div>
              <span className="muted" style={{ fontSize: 11 }}>{s.created_at?.split('T')[0]}</span>
            </div>
            <p style={{ margin: '12px 0', fontSize: 14, lineHeight: 1.6, color: '#1e293b' }}>{s.scenario_text}</p>
            <div style={{ background: '#f8fafc', padding: 12, borderRadius: 6, marginBottom: 12 }}>
              <p style={{ fontSize: 12, color: '#64748b', marginBottom: 4 }}>Actual outcome: <strong>{s.actual_outcome}</strong></p>
              <p style={{ fontSize: 13, color: '#475569' }}>{s.actual_outcome_desc}</p>
            </div>
            {filter === 'draft' && (
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-success" onClick={() => handlePromote(s.id)}>Promote to live</button>
                <button className="btn btn-danger" onClick={() => handleReject(s.id)}>Reject</button>
              </div>
            )}
          </div>
        ))}
    </div>
  );
}

export default App;
